import { HttpClient } from '@angular/common/http'
import { Injectable, OnDestroy } from '@angular/core'
import * as pako from 'pako'
import { BehaviorSubject, EMPTY, Subject, throwError } from 'rxjs'
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators'
import { webSocket, WebSocketSubject } from 'rxjs/webSocket'

import { environment } from '../../../environments/environment'
import { BetConfigService } from './bet-config.service'
import { HelperService } from './helper.service'
import { LiveUpdateService } from './live-update.service'

const FEED_URL = environment.feedUrl
const WEBSOCKET_URL = environment.wsUrl

type TopicMode = 'merge' | 'replace' | 'replaceOwner'
interface TopicMessage {
	topics: string[]
	topicMode?: TopicMode
	topicOwner?: string
}

@Injectable({
	providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
	connectionStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

	/* eslint-disable @typescript-eslint/no-explicit-any */
	private webSocket$: WebSocketSubject<any> | null = null

	private feedUrl = ''

	private sessionId = ''

	private pingInterval: any = null

	PING_INTERVAL = 10_000 // 10 seconds between pings

	PONG_TIMEOUT = 45_000 // Enforced only after this connection has confirmed pong support.

	private hasReceivedPong = false

	private lastPongTime = 0

	private connectionSubscription: any = null

	private reconnectTimer: ReturnType<typeof setTimeout> | null = null

	private reconnectAttempts = 0

	private readonly reconnectDelays = [1_000, 2_000, 5_000]

	private readonly destroy$ = new Subject<void>()

	isConnecting = false

	private currentTopics: string[] = []

	private readonly topicOwners = new Map<string, string[]>()

	private connectionGeneration = 0

	constructor(
		private readonly httpClient: HttpClient,
		private readonly helperService: HelperService,
		private readonly liveUpdateService: LiveUpdateService,
		private readonly betConfigService: BetConfigService,
	) {}

	// Function to initiate user registration and WebSocket connection
	startConnection() {
		if (this.webSocket$ && this.connectionStatus$.value) {
			return this.webSocket$
		}

		if (this.reconnectTimer) {
			console.log('WebSocket reconnect already scheduled')
			return EMPTY
		}

		if (this.isConnecting) {
			console.log('WebSocket connection already in progress')
			return EMPTY
		}

		this.isConnecting = true

		return this.registerUser().pipe(
			switchMap(() => this.connectToFeed()),
			takeUntil(this.destroy$),
			tap({
				next: () => {
					this.isConnecting = false
				},
				error: () => {
					this.isConnecting = false
				},
			}),
			catchError((error) => {
				console.error('WebSocket start failed:', error)
				this.isConnecting = false
				this.scheduleReconnect()
				return EMPTY
			}),
		)
	}

	// Function to send a message through WebSocket
	/* eslint-disable @typescript-eslint/no-explicit-any */
	sendMessage(message: any): void {
		// Store topics even while reconnecting/opening so openObserver can re-subscribe.
		this.trackMessageTopics(message)
		this.sendOutboundMessage(message)
	}

	private trackMessageTopics(message: any): void {
		if (!this.isTopicMessage(message)) return

		const nextTopics = [...new Set(message.topics)]
		if (message.topicMode === 'replaceOwner' && message.topicOwner) {
			this.replaceOwnerTopics(message.topicOwner, nextTopics)
			return
		}

		this.updateCurrentTopics(message.topicMode, nextTopics)
	}

	private replaceOwnerTopics(topicOwner: string, nextTopics: string[]): void {
		if (nextTopics.length === 0) {
			this.topicOwners.delete(topicOwner)
		} else {
			this.topicOwners.set(topicOwner, nextTopics)
		}
		this.currentTopics = this.getOwnedTopics()
	}

	private updateCurrentTopics(topicMode: TopicMode | undefined, nextTopics: string[]): void {
		// Default merge keeps backward compatibility for existing topic callers.
		if (topicMode === 'replace') {
			this.topicOwners.clear()
		}
		this.currentTopics = nextTopics.length === 0 || topicMode === 'replace' ? nextTopics : [...new Set([...this.currentTopics, ...nextTopics])]
	}

	private sendOutboundMessage(message: any): void {
		if (this.webSocket$ && this.connectionStatus$.value) {
			this.webSocket$.next(this.getOutboundMessage(message))
		} else if (this.isConnecting || this.reconnectTimer) {
			return
		} else {
			console.error('WebSocket connection is not established.')
		}
	}

	// Register the user and get the WebSocket URL
	private registerUser() {
		const registerUrl = `${FEED_URL}/register`
		const userFields = this.helperService.getUserData()?.user_fields
		const userId = userFields?.ID
		const domainId = userFields?.DOMAIN_ID

		return this.httpClient
			.post<{
				url: string
			}>(registerUrl, { user_id: userId || '', domain_id: domainId || '' })
			.pipe(
				tap((response) => {
					this.feedUrl = `${WEBSOCKET_URL}${response.url}`
					this.sessionId = response.url.split('ws/')[1]
				}),
				catchError((error) => throwError(() => new Error('Failed to register: ' + error.message))),
			)
	}

	// Connect to the WebSocket server
	private connectToFeed() {
		if (!this.feedUrl) {
			return throwError(() => new Error('WebSocket URL not found. Make sure to register first.'))
		}

		// Clean up existing connection before opening a new socket.
		this.connectionGeneration += 1
		const connectionGeneration = this.connectionGeneration
		this.cleanupConnection(false, false)
		this.clearScheduledReconnect()
		this.isConnecting = true

		this.webSocket$ = webSocket({
			url: this.feedUrl,
			binaryType: 'arraybuffer', // Important for binary data
			WebSocketCtor: class extends WebSocket {
				constructor(url: string, protocols?: string | string[]) {
					super(url, protocols)
					Object.defineProperty(this, 'withCredentials', { value: true })
					this.binaryType = 'arraybuffer'
				}
			},
			openObserver: {
				next: () => {
					if (!this.isActiveConnection(connectionGeneration)) {
						return
					}

					this.isConnecting = false
					this.clearScheduledReconnect()
					this.reconnectAttempts = 0
					this.hasReceivedPong = false
					this.lastPongTime = 0
					this.connectionStatus$.next(true)
					console.log('WebSocket connection established')
					// Re-subscribe to topics after reconnection
					if (this.currentTopics.length > 0) {
						console.log('🔄 Re-subscribing to topics:', this.currentTopics)
						this.webSocket$?.next({ topics: this.currentTopics })
					}
				},
			},
			closeObserver: {
				next: () => {
					if (!this.isActiveConnection(connectionGeneration)) {
						return
					}

					console.log('WebSocket connection closed')
					this.handleConnectionLoss()
				},
			},
			deserializer: (e: MessageEvent) => {
				try {
					if (typeof e.data === 'string') {
						return e.data === 'ping' || e.data === 'pong' ? e.data : JSON.parse(e.data)
					}
					if (e.data instanceof ArrayBuffer) {
						const decompressed = pako.inflate(new Uint8Array(e.data), { to: 'string' })
						if (decompressed === 'ping' || decompressed === '"ping"' || decompressed === 'pong' || decompressed === '"pong"') {
							return decompressed.replace(/"/g, '')
						}
						return JSON.parse(decompressed)
					}
					console.log('Unknown message type:', typeof e.data)
					return e.data
				} catch (error) {
					console.error('Deserialization error:', error)
					if (e.data instanceof ArrayBuffer) {
						// Try to see what's in the raw data
						const rawString = new TextDecoder().decode(new Uint8Array(e.data))
						console.error('🔧 Raw data as string:', rawString.substring(0, 200))
					}
					return { error: 'Deserialization failed' }
				}
			},
		})

		this.pingInterval = setInterval(() => {
			if (this.webSocket$ && this.connectionStatus$.value) {
				if (this.hasReceivedPong && Date.now() - this.lastPongTime > this.PONG_TIMEOUT) {
					console.warn('⚠️ No pong from server — reconnecting...')
					this.handleConnectionLoss()
					return
				}

				this.sendMessage('ping')
			}
		}, this.PING_INTERVAL)

		// Subscribe to the WebSocket to keep it alive
		this.connectionSubscription = this.webSocket$.subscribe({
			next: (data) => {
				if (!this.isActiveConnection(connectionGeneration)) {
					return
				}

				if (data === 'pong') {
					this.hasReceivedPong = true
					this.lastPongTime = Date.now()
					// console.log('✅ Pong received')
					return
				}
				// Skip ping messages to reduce processing overhead
				if (data !== 'ping') {
					if (data?.type === 'bet-config') {
						this.betConfigService.updateBetConfig(data.data?.BET_CONFIG)
					}
					this.liveUpdateService.setData(data)
				}
			},
			error: (error) => {
				if (!this.isActiveConnection(connectionGeneration)) {
					return
				}

				console.error('WebSocket error:', error)
				this.handleConnectionLoss()
			},
			complete: () => {
				if (!this.isActiveConnection(connectionGeneration)) {
					return
				}

				console.log('WebSocket connection completed')
				this.handleConnectionLoss()
			},
		})

		return this.webSocket$
	}

	private handleConnectionLoss(): void {
		this.scheduleReconnect()
		this.cleanupConnection()
	}

	private scheduleReconnect(): void {
		if (this.reconnectTimer || !this.helperService.getUserData()?.access_token) {
			return
		}

		const delay = this.reconnectDelays[Math.min(this.reconnectAttempts, this.reconnectDelays.length - 1)]
		this.reconnectAttempts += 1

		console.log(`Scheduling WebSocket reconnect in ${delay}ms`)

		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null
			this.startConnection().subscribe({
				error: (error) => console.error('Scheduled WebSocket reconnect failed:', error),
			})
		}, delay)
	}

	private clearScheduledReconnect(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer)
			this.reconnectTimer = null
		}
	}

	private cleanupConnection(emitStatus = true, invalidateConnection = true): void {
		// Clear ping interval
		if (this.pingInterval) {
			clearInterval(this.pingInterval)
			this.pingInterval = null
		}

		// Unsubscribe from WebSocket
		if (this.connectionSubscription) {
			this.connectionSubscription.unsubscribe()
			this.connectionSubscription = null
		}

		// Close WebSocket connection
		if (this.webSocket$) {
			const socket = this.webSocket$
			this.webSocket$ = null
			if (invalidateConnection) {
				this.connectionGeneration += 1
			}

			try {
				socket.complete()
			} catch (error) {
				console.warn('Error completing WebSocket:', error)
			}
		}

		this.isConnecting = false
		if (emitStatus && this.connectionStatus$.value) {
			this.connectionStatus$.next(false)
		}
	}

	private isTopicMessage(message: unknown): message is TopicMessage {
		return typeof message === 'object' && message !== null && Array.isArray((message as TopicMessage).topics)
	}

	private isActiveConnection(connectionGeneration: number): boolean {
		return connectionGeneration === this.connectionGeneration
	}

	private getOutboundMessage(message: any): any {
		if (!this.isTopicMessage(message) || (!message.topicMode && !message.topicOwner)) {
			return message
		}

		const outboundMessage = {
			...message,
			topics: message.topicMode === 'replaceOwner' && message.topicOwner ? this.currentTopics : message.topics,
		}
		delete outboundMessage.topicMode
		delete outboundMessage.topicOwner
		return outboundMessage
	}

	private getOwnedTopics(): string[] {
		return [...new Set([...this.topicOwners.values()].flat())]
	}

	ngOnDestroy() {
		console.log('WebSocketService destroying...')
		this.destroy$.next()
		this.destroy$.complete()

		this.clearScheduledReconnect()
		this.currentTopics = []
		this.topicOwners.clear()
		this.cleanupConnection()

		// Clear BehaviorSubject to free memory
		this.connectionStatus$.next(false)
	}
}
